import React from "react";
import { useTranslation } from "react-i18next";
import {
  IconButton, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText,
  Button, Box, Typography
} from "@mui/material";
import TranslateIcon from "@mui/icons-material/Translate";
import CheckIcon from "@mui/icons-material/Check";
import { SUPPORTED_LANGUAGES } from "../../i18n";

/**
 * Language toggle. Renders an icon button (default) or a labelled button that
 * opens a menu of the supported languages. The chosen language is persisted by
 * i18next's language detector (localStorage key `vitrine_lang`), so it survives
 * reloads and is restored on the next visit.
 *
 * Props:
 *   variant: "icon" (default) | "button"  — "button" also shows the flag + native name.
 *   size, color — forwarded to the trigger (MUI IconButton/Button props).
 *   languages — optional array of codes to OFFER, e.g. ["en","fr"]. Defaults to
 *     every supported language. Use it where a section does not ship all four:
 *     the public marketing site (`marketing`/`company`/`product`/`resources`) is
 *     deliberately en/fr only, and listing Kiswahili there would give the visitor
 *     a control that visibly does nothing, since i18next would fall back to
 *     English on the very page they are reading.
 */
const LanguageSwitcher = ({
  variant = "icon",
  size = "small",
  color = "inherit",
  languages,
}) => {
  const { i18n, t } = useTranslation("common");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  // Which languages this instance offers. An unknown code in `languages` is
  // ignored rather than rendered as a broken entry; an empty/whole result falls
  // back to the full list so a bad prop can never leave the menu empty.
  const offered = React.useMemo(() => {
    if (!languages || !languages.length) return SUPPORTED_LANGUAGES;
    const subset = SUPPORTED_LANGUAGES.filter((l) => languages.includes(l.code));
    return subset.length ? subset : SUPPORTED_LANGUAGES;
  }, [languages]);

  // Resolve the active language to one of our supported entries (handles
  // region variants like "fr-FR" → "fr" via base-code match).
  //
  // Resolved against the FULL list, not `offered`, on purpose: someone who chose
  // Kiswahili in the app and then opens a marketing page should still see
  // "Kiswahili" as their current language. Showing "English" there would be a
  // lie, and switching them silently would lose their choice.
  const base = (i18n.language || "en").split("-")[0];
  const current =
    SUPPORTED_LANGUAGES.find((l) => l.code === base) || SUPPORTED_LANGUAGES[0];

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSelect = (code) => {
    i18n.changeLanguage(code);
    handleClose();
  };

  return (
    <>
      <Tooltip title={t("language.select")}>
        {variant === "button" ? (
          <Button
            onClick={handleOpen}
            color={color}
            size={size}
            startIcon={<TranslateIcon />}
            aria-label={t("language.select")}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            sx={{ textTransform: "none" }}
          >
            <Box component="span" sx={{ mr: 0.5 }}>{current.flag}</Box>
            {current.nativeName}
          </Button>
        ) : (
          <IconButton
            onClick={handleOpen}
            color={color}
            size={size}
            aria-label={t("language.select")}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
          >
            <TranslateIcon />
          </IconButton>
        )}
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ dense: true }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ px: 2, py: 0.5, display: "block" }}
        >
          {t("language.label")}
        </Typography>
        {offered.map((lng) => {
          const selected = lng.code === current.code;
          return (
            <MenuItem
              key={lng.code}
              selected={selected}
              onClick={() => handleSelect(lng.code)}
              lang={lng.code}
            >
              <ListItemIcon sx={{ minWidth: 32, fontSize: "1.1rem" }}>
                {lng.flag}
              </ListItemIcon>
              <ListItemText
                primary={lng.nativeName}
                secondary={lng.nativeName !== lng.label ? lng.label : undefined}
              />
              {selected && (
                <CheckIcon fontSize="small" color="primary" sx={{ ml: 1 }} />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default LanguageSwitcher;
